import { getComplexList } from 'api/offer';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setBldInfo, setModal } from 'redux/store/mapReducer';
import styled from 'styled-components';
import color from 'styles/colors';
import { MAP_TYPES } from 'types/map';
import { ComplexDetails } from 'types/offer';
import { HighLightedText } from './HighLightedText';

const AddressSearch = () => {
  const [bldNm, setBldNm] = useState('');
  const [bldList, setBldList] = useState<ComplexDetails[]>([]);
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const blurInputWhenClickOtherElement = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        inputRef.current.blur();
      }
    };

    document.addEventListener('click', (event) => {
      blurInputWhenClickOtherElement(event);
    });
    return () => {
      document.removeEventListener('click', blurInputWhenClickOtherElement);
    };
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (bldNm) {
        getComplexSearchList(bldNm);
      } else {
        setBldList([]);
      }
    }, 150);
    return () => clearTimeout(delayDebounceFn);
  }, [bldNm]);

  const getComplexSearchList = async (bldNm: string) => {
    const response = await getComplexList(bldNm);
    const { data, error } = response;

    if (error) {
      setBldList([]);
      return;
    }

    if (data) {
      setBldList(data);
    }
  };

  const handleChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBldNm(e.target.value);
  };

  const handleClickSearchFilter = () => {
    showComplexModalOnApp();
  };

  function handleClickComplexItem(item: ComplexDetails) {
    setBldList([]);
    setBldNm('');

    dispatch(setBldInfo(item));
  }

  const showComplexModalOnApp = () => {
    if (window?.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage('modal');
      dispatch(setModal(false));
    }
  };

  return (
    <>
      <Container>
        <SearchBox>
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder="아파트 단지명, 주소를 입력해주세요."
            name="search"
            value={bldNm}
            onChange={handleChangeSearchInput}
          />
          <SearchFilter onClick={handleClickSearchFilter}>
            <FilterImage src="/images/find_gap_property/ic_sliders_sm.svg" />
            <FilterLabel>필터</FilterLabel>
          </SearchFilter>
        </SearchBox>
      </Container>
      {!!bldList.length && (
        <FilterContainer>
          <BlankDiv />
          <BldListBox>
            {bldList.map((item) => {
              return (
                <DisplayFilteredValue
                  key={item.id}
                  onClick={() => {
                    handleClickComplexItem(item);
                  }}
                >
                  <BldName
                    text={item.bldNm}
                    highlightText={bldNm}
                    defaultColor="#333"
                    highlightColor={color.primary}
                    size={14}
                  />
                  <TextWrapper>
                    <ComplexCategoryType>
                      {item.categoryType} &middot;{' '}
                    </ComplexCategoryType>
                    <ComplexDistrict
                      text={item.district}
                      highlightText={bldNm}
                      defaultColor="#9e9e9e"
                      highlightColor={color.primary}
                      size={12}
                    />
                  </TextWrapper>
                  <ComplexRoadAddress
                    text={item.roadAddress}
                    highlightText={bldNm}
                    defaultColor="#9e9e9e"
                    highlightColor={color.primary}
                    size={12}
                  />
                </DisplayFilteredValue>
              );
            })}
          </BldListBox>
        </FilterContainer>
      )}
    </>
  );
};

export default AddressSearch;

AddressSearch.defaultProps = {
  mapType: MAP_TYPES.NORMAL,
};

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: absolute;
  z-index: 2;
  padding-top: 16px;
`;

const SearchBox = styled.div`
  display: flex;
  width: 90%;
  box-shadow: 0px 0px 8px rgba(0, 30, 81, 0.12);
`;

const BlankDiv = styled.div`
  height: 72px;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  left: 0;
  right: 0;
  background-color: white;
  z-index: 1;
  max-height: 100vh;
  overflow: hidden;
`;

const BldListBox = styled.div`
  flex-direction: column;
  width: 100vw;
  display: flex;
  position: relative;
  align-items: center;
  overflow-y: auto;
  max-height: calc(100vh - 72px);
  background-color: white;
`;

const DisplayFilteredValue = styled.div`
  width: 90%;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-top: 1px solid #f5f5f5;
  border-bottom: 1px solid #f5f5f5;
  padding: 12px 0px;
`;

const SearchInput = styled.input`
  height: 40px;
  width: 80%;
  border-radius: 8px 0px 0px 8px;
  border: solid 1px #ffffff;
  background-color: #ffffff;
  font-size: 14px;
  text-align: left;
  color: #9e9e9e;
  outline: none;
  background-image: url('/images/find_gap_property/ic_find_sm_disabled.svg');
  background-repeat: no-repeat;
  background-position: 12px;
  background-size: auto;
  padding-left: 38px;
  border-right: 1px solid #e6e6e6;
`;

const SearchFilter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20%;
  border-radius: 0px 8px 8px 0px;
  border: solid 1px #ffffff;
  background-color: #ffffff;
  cursor: pointer;
`;

const FilterImage = styled.img``;

const FilterLabel = styled.div`
  font-weight: bold;
  font-size: 12px;
  color: #333333;
  margin-left: 4px;
`;

const BldName = styled(HighLightedText)``;

const ComplexRoadAddress = styled(HighLightedText)``;

const ComplexDistrict = styled(ComplexRoadAddress)`
  margin: 5px 0px;
`;

const ComplexCategoryType = styled.span`
  font-size: 12px;
  color: #9e9e9e;
  font-weight: bold;
`;

const TextWrapper = styled.span``;
